import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
        primary:
          "bg-primary/15 dark:bg-primary/25 text-primary dark:text-primary border-transparent",
        neutral: "bg-muted text-muted-foreground border-transparent",
        danger:
          "bg-destructive/15 dark:bg-destructive/25 text-destructive dark:text-red-400 border-transparent",
        success:
          "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 border-transparent",
        warning:
          "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-transparent",
        pink: "bg-pink-200 dark:bg-pink-800/40 text-pink-800 dark:text-pink-300 border-transparent",
        lime: "bg-lime-200 dark:bg-lime-800/40 text-lime-800 dark:text-lime-300 border-transparent",
        yellow:
          "bg-yellow-200 dark:bg-yellow-800/40 text-yellow-800 dark:text-yellow-300 border-transparent",
        lavender:
          "bg-lavender-200 dark:bg-lavender-200/40 text-lavender-800 dark:text-lavender-700 border-transparent",
        "outline-primary":
          "bg-transparent border border-primary/30 text-primary dark:text-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
